import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowDown, ArrowUp, ClipboardCheck, Copy, GripHorizontal, Image as ImageIcon, MoreVertical, Trash2, Upload,
} from 'lucide-react';
import { esPregunta, maximoDe, youtubeId } from '../../../lib/formularios';
import { Switch } from '../../ui/switch';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { useEditor } from './contexto';
import { ImagenAdjunta, TextoCreciente } from './controles';
import CuerpoPregunta from './CuerpoPregunta';
import EditorValidacion from './EditorValidacion';
import ClaveRespuestas from './ClaveRespuestas';
import MenuPregunta from './MenuPregunta';
import SelectorTipo from './SelectorTipo';

/**
 * Una tarjeta del formulario (pregunta, título, imagen, vídeo o sección).
 * Inactiva se lee como la verá quien responde; activa (clic) muestra los controles de edición.
 * Solo una tarjeta está activa a la vez: es la que marca dónde se insertan las nuevas.
 */
export default function TarjetaItem({ item, registrar, arrastre, onAsa }) {
  const ed = useEditor();
  const { puedeEditar, activo, setActivo, esCuestionario } = ed;
  const activa = puedeEditar && activo === item.id;
  const [modoClave, setModoClave] = useState(false);
  const [verDescripcion, setVerDescripcion] = useState(!!item.descripcion);
  const tituloRef = useRef(null);
  const pregunta = esPregunta(item);

  useEffect(() => {
    if (!activa) setModoClave(false);
  }, [activa]);

  useEffect(() => {
    if (item.descripcion) setVerDescripcion(true);
  }, [item.descripcion]);

  // Recién añadida: el cursor va al título, como en Google
  useEffect(() => {
    if (!activa || ed.nuevoFoco !== item.id) return;
    const el = tituloRef.current;
    if (el) {
      el.focus();
      el.select?.();
    }
    ed.limpiarNuevoFoco();
  }, [activa, ed, item.id]);

  const arrastrando = arrastre?.id === item.id;
  const clases = [
    'fm-tarjeta', 'fe-tarjeta',
    activa && 'is-activa',
    item.tipo === 'seccion' && 'fm-tarjeta-encabezado fe-tarjeta-seccion',
    arrastrando && 'is-arrastrando',
    arrastre && arrastre.antesDe === item.id && 'fe-soltar-antes',
    !activa && puedeEditar && 'fe-tarjeta-inactiva',
  ].filter(Boolean).join(' ');

  const activar = () => {
    if (!activa && puedeEditar) setActivo(item.id);
  };

  return (
    <div
      ref={(el) => registrar(item.id, el)}
      className={clases}
      data-item={item.id}
      style={arrastrando ? { transform: `translateY(${arrastre.dy}px)` } : undefined}
      onMouseDown={activar}
      onFocusCapture={activar}
      {...(!activa && puedeEditar ? {
        role: 'button',
        tabIndex: 0,
        'aria-label': `Editar: ${item.titulo || 'sin título'}`,
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            activar();
          }
        },
      } : {})}
    >
      {puedeEditar && item.tipo !== 'seccion' && (
        <div
          className="fe-asa"
          onPointerDown={(e) => onAsa(e, item.id)}
          aria-hidden="true"
          title="Arrastrar para mover"
        >
          <GripHorizontal />
        </div>
      )}

      {activa && modoClave && pregunta
        ? <ClaveRespuestas item={item} onListo={() => setModoClave(false)} />
        : activa
          ? <Edicion item={item} tituloRef={tituloRef} verDescripcion={verDescripcion} />
          : <Vista item={item} />}

      {activa && !modoClave && (
        <PieTarjeta
          item={item}
          verDescripcion={verDescripcion}
          onVerDescripcion={setVerDescripcion}
          onClave={esCuestionario && pregunta ? () => setModoClave(true) : null}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ edición */

function Edicion({ item, tituloRef, verDescripcion }) {
  const { cambiarItem, subirImagen, cambiarTipoItem } = useEditor();
  const set = (campo) => (e) => cambiarItem(item.id, { [campo]: e.target.value }, { clave: `${item.id}:${campo}` });
  const ponerImagen = async () => {
    const img = await subirImagen();
    if (img) cambiarItem(item.id, { imagen: img });
  };

  if (esPregunta(item)) {
    return (
      <div className="fe-edicion">
        <div className="fe-pregunta-cabeza">
          <div className="fe-pregunta-titulo">
            <TextoCreciente
              inputRefExterno={tituloRef}
              className="fe-input-titulo fm-titulo-pregunta"
              value={item.titulo}
              placeholder="Pregunta"
              aria-label="Pregunta"
              onChange={set('titulo')}
            />
          </div>
          <button type="button" className="fe-icono-btn" onClick={ponerImagen} aria-label="Añadir imagen a la pregunta" title="Añadir imagen">
            <ImageIcon />
          </button>
          <SelectorTipo tipo={item.tipo} onCambiar={(t) => cambiarTipoItem(item.id, t)} />
        </div>
        <ImagenAdjunta
          imagen={item.imagen}
          alt={item.titulo}
          className="fe-imagen-pregunta"
          onQuitar={() => cambiarItem(item.id, { imagen: null })}
          onCambiar={ponerImagen}
        />
        {verDescripcion && (
          <TextoCreciente
            className="fe-input-descripcion fm-descripcion"
            value={item.descripcion}
            placeholder="Descripción"
            aria-label="Descripción de la pregunta"
            onChange={set('descripcion')}
          />
        )}
        <CuerpoPregunta item={item} editable />
        <EditorValidacion item={item} />
      </div>
    );
  }

  if (item.tipo === 'imagen') {
    return (
      <div className="fe-edicion">
        <TextoCreciente
          inputRefExterno={tituloRef}
          className="fe-input-titulo fm-titulo-pregunta"
          value={item.titulo}
          placeholder="Título de la imagen (opcional)"
          aria-label="Título de la imagen"
          onChange={set('titulo')}
        />
        {item.imagen?.id ? (
          <ImagenAdjunta
            imagen={item.imagen}
            alt={item.titulo}
            className="fe-imagen-bloque"
            onCambiar={ponerImagen}
            onQuitar={() => cambiarItem(item.id, { imagen: null })}
          />
        ) : (
          <button type="button" className="fe-subir-imagen" onClick={ponerImagen}>
            <Upload /> Subir imagen
          </button>
        )}
      </div>
    );
  }

  if (item.tipo === 'video') {
    const id = item.video?.youtube_id || youtubeId(item.video?.url);
    return (
      <div className="fe-edicion">
        <TextoCreciente
          inputRefExterno={tituloRef}
          className="fe-input-titulo fm-titulo-pregunta"
          value={item.titulo}
          placeholder="Título del vídeo (opcional)"
          aria-label="Título del vídeo"
          onChange={set('titulo')}
        />
        <input
          className="fm-campo-linea"
          value={item.video?.url || ''}
          placeholder="Pega el enlace de YouTube"
          aria-label="Enlace de YouTube"
          onChange={(e) => cambiarItem(
            item.id,
            { video: { url: e.target.value, youtube_id: youtubeId(e.target.value) } },
            { clave: `${item.id}:video` },
          )}
        />
        {item.video?.url && !id && <div className="fe-aviso-campo">No es un enlace de YouTube válido.</div>}
        {id && (
          <div className="fm-video fe-video">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${id}`}
              title={item.titulo || 'Vídeo'}
              allow="accelerometer; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}
      </div>
    );
  }

  // titulo y seccion
  return (
    <div className="fe-edicion">
      <TextoCreciente
        inputRefExterno={tituloRef}
        className={item.tipo === 'seccion' ? 'fe-input-titulo fe-input-titulo-seccion' : 'fe-input-titulo fm-titulo-pregunta'}
        value={item.titulo}
        placeholder={item.tipo === 'seccion' ? 'Sección sin título' : 'Título'}
        aria-label="Título"
        onChange={set('titulo')}
      />
      <TextoCreciente
        className="fe-input-descripcion fm-descripcion"
        value={item.descripcion}
        placeholder="Descripción (opcional)"
        aria-label="Descripción"
        onChange={set('descripcion')}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ vista */

function Vista({ item }) {
  const { esCuestionario } = useEditor();
  if (esPregunta(item)) {
    return (
      <div className="fe-vista">
        <div className="fm-titulo-pregunta fe-vista-titulo">
          {item.titulo || <span className="fe-sin-titulo">Pregunta</span>}
          {item.obligatoria && <span className="fm-obligatoria" aria-label="obligatoria">*</span>}
          {esCuestionario && <span className="fe-puntos-etiqueta">{maximoDe(item)} {maximoDe(item) === 1 ? 'punto' : 'puntos'}</span>}
        </div>
        {item.descripcion && <div className="fm-descripcion fe-vista-descripcion">{item.descripcion}</div>}
        <ImagenAdjunta imagen={item.imagen} alt={item.titulo} className="fe-imagen-pregunta" />
        <CuerpoPregunta item={item} editable={false} />
      </div>
    );
  }
  if (item.tipo === 'imagen') {
    return (
      <div className="fe-vista">
        {item.titulo && <div className="fm-titulo-pregunta fe-vista-titulo">{item.titulo}</div>}
        {item.imagen?.id
          ? <ImagenAdjunta imagen={item.imagen} alt={item.titulo} className="fe-imagen-bloque" />
          : <div className="fe-sin-titulo">Imagen sin subir</div>}
      </div>
    );
  }
  if (item.tipo === 'video') {
    const id = item.video?.youtube_id || youtubeId(item.video?.url);
    return (
      <div className="fe-vista">
        {item.titulo && <div className="fm-titulo-pregunta fe-vista-titulo">{item.titulo}</div>}
        {id ? (
          <div className="fe-video-miniatura">
            <img src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`} alt={item.titulo || 'Vídeo de YouTube'} loading="lazy" />
          </div>
        ) : <div className="fe-sin-titulo">Vídeo sin enlace</div>}
      </div>
    );
  }
  return (
    <div className="fe-vista">
      <div className={item.tipo === 'seccion' ? 'fm-titulo-form fe-titulo-seccion' : 'fm-titulo-pregunta fe-vista-titulo'}>
        {item.titulo || <span className="fe-sin-titulo">{item.tipo === 'seccion' ? 'Sección sin título' : 'Título'}</span>}
      </div>
      {item.descripcion && <div className="fm-descripcion fe-vista-descripcion">{item.descripcion}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ pie */

function PieTarjeta({ item, verDescripcion, onVerDescripcion, onClave }) {
  const ed = useEditor();
  const pregunta = esPregunta(item);

  if (item.tipo === 'seccion') {
    return (
      <div className="fe-pie-tarjeta">
        <span className="flex-1" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="fe-icono-btn" aria-label="Opciones de la sección" title="Opciones de la sección">
              <MoreVertical />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="fe-menu">
            <DropdownMenuItem onSelect={() => ed.duplicarSeccion(item.id)}><Copy /> Duplicar sección</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => ed.moverSeccion(item.id, -1)}><ArrowUp /> Mover sección hacia arriba</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => ed.moverSeccion(item.id, 1)}><ArrowDown /> Mover sección hacia abajo</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => ed.borrarSeccion(item.id, { conPreguntas: false })}>
              Combinar con la sección anterior
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => ed.borrarSeccion(item.id, { conPreguntas: true })} className="fe-menu-peligro">
              <Trash2 /> Eliminar sección y sus preguntas
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div className="fe-pie-tarjeta">
      {onClave && (
        <button type="button" className="fe-btn-texto fe-btn-clave" onClick={onClave}>
          <ClipboardCheck /> Clave de respuestas
          <span className="fe-texto-2">({maximoDe(item)} {maximoDe(item) === 1 ? 'punto' : 'puntos'})</span>
        </button>
      )}
      <span className="flex-1" />
      <button type="button" className="fe-icono-btn" onClick={() => ed.duplicar(item.id)} aria-label="Duplicar" title="Duplicar">
        <Copy />
      </button>
      <button type="button" className="fe-icono-btn" onClick={() => ed.borrarItem(item.id)} aria-label="Eliminar" title="Eliminar">
        <Trash2 />
      </button>
      {pregunta && (
        <>
          <span className="fe-pie-sep" aria-hidden="true" />
          <label className="fe-obligatoria-switch" htmlFor={`obl-${item.id}`}>
            Obligatoria
            <Switch
              id={`obl-${item.id}`}
              checked={!!item.obligatoria}
              onCheckedChange={(v) => ed.cambiarItem(item.id, { obligatoria: v })}
            />
          </label>
        </>
      )}
      <MenuPregunta item={item} verDescripcion={verDescripcion} onVerDescripcion={onVerDescripcion} />
    </div>
  );
}
